import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../redux';
import { routesByRole } from '../../../app/routes/routes.config';
import { AppRoute } from '../../../app/routes/routes.types';
import { FaBeer } from 'react-icons/fa';

const mapNavChildren = (
  children: AppRoute[] | undefined,
  parentIndex: number,
  pathname: string,
  t: (key: string) => string,
) =>
  (children || [])
    .filter(child => child.pageTitle)
    .map((child, childIndex) => ({
      id: `${parentIndex}-${childIndex}`,
      title: t(child.pageTitle!),
      path: child.route || '',
      pathKey: child.pathKey || '',
      isActive:
        (child.isActive && child.isActive(pathname)) || false,
      icon: child.renderIcon ? child.renderIcon(pathname, false) : undefined,
      children: [],
      type: child.pathKey || '',
    }));

/**
 * Hook for getting navigation links based on user role
 */
const useLinks = () => {
  const { t } = useTranslation();
  const userRole = useAppSelector(
    (state: any) => state.authorization?.user?.role || 'none',
  );
  const effectiveRole = userRole === 'none' ? 'none' : 'owner';
  const location = useLocation();

  const links = useMemo(() => {
    if (effectiveRole !== 'none') {
      return (routesByRole[effectiveRole] || [])
        .filter(route => route.pageTitle && route.isInNavigation)
        .map((route, index) => ({
          id: index,
          title: t(route.pageTitle!),
          path: route.route || '',
          pathKey: route.pathKey || '',
          className: route.className || '',
          isActive:
            (route.isActive && route.isActive(location.pathname)) || false,
          icon: route.renderIcon ? (
            route.renderIcon(location.pathname, false)
          ) : (
            <FaBeer />
          ),
          count: 0,
          children: mapNavChildren(
            route.children,
            index,
            location.pathname,
            t,
          ),
          type: route.pathKey || route.route?.split('/')[1] || '',
        }));
    }

    return [];
  }, [location.pathname, effectiveRole, t]);

  return links;
};

export default useLinks;
